import { Dialog, DialogId, DialogType } from '../../../../src/feature/narrator/narrator.model';
import { ItemId } from '../../inventory/items';
import { hasItem } from '../../inventory/helpers';

// Fast-day fish the priest will take, cheapest first (so the traveller keeps the
// better catch). Each fish has its own give-node so the consume effect stays static.
const FISH_ORDER: ItemId[] = ['chub', 'trout', 'eel', 'pike'];

const GIVE_NODE_FOR_FISH: Record<string, DialogId> = {
  chub: 'give_chub',
  trout: 'give_trout',
  eel: 'give_eel',
  pike: 'give_pike',
};

const hasAnyFish = (): boolean => FISH_ORDER.some(hasItem);

// He gives the blessed charm once. The offer only shows while the traveller has a
// fish to give and does not yet carry the charm.
const canOfferFish = (): boolean => hasAnyFish() && !hasItem('charm');

// With no fish to give (and no charm yet), greeting him catches the priest
// muttering to himself about needing a fish for the fast day — an indirect hint
// to bring one. He does NOT mention the river here (the danger is only revealed
// when he gives the charm / when asked about the water).
const greetTarget = (): DialogId => (!hasItem('charm') && !hasAnyFish() ? 'mutter' : 'greet');

const resolveGiveNode = (): DialogId => {
  const fish = FISH_ORDER.find(hasItem);
  return fish ? GIVE_NODE_FOR_FISH[fish] : 'start';
};

// Seeing the traveller has been at the water, the priest blesses a small charm
// for protection (against the road, and the thing in the river) instead of coin.
const blessing =
  "You place the fish in his hands. He sees you have been by the water. 'There are things in that river best not met, my child. Take this charm. I have blessed it, and may Saint Willibrord keep you safe.'";

const giveNode = (fish: ItemId): Dialog => ({
  id: GIVE_NODE_FOR_FISH[fish],
  type: DialogType.dialog,
  text: blessing,
  effects: [
    { type: 'consume', item: fish, quantity: 1 },
    { type: 'grant', item: 'charm', quantity: 1 },
  ],
  options: [{ text: 'Thank you, father.', nextDialogId: 'start' }],
});

export const dialog: Dialog[] = [
  {
    id: 'start',
    type: DialogType.description,
    text: 'The priest stands at the chapel door in a plain grey robe. He folds his hands when he sees you and waits with a calm face.',
    options: [
      { text: 'Greet him.', nextDialogId: greetTarget },
      { text: 'Ask about the chapel.', nextDialogId: 'church' },
    ],
  },
  {
    id: 'greet',
    type: DialogType.dialog,
    text: 'Peace be with you, my child. By Saint Willibrord, it is good to see a traveller at this door.',
    options: [
      { text: 'What do you do here?', nextDialogId: 'church' },
      { text: 'I have a fish for you, father.', nextDialogId: resolveGiveNode, condition: canOfferFish },
    ],
  },
  {
    id: 'church',
    type: DialogType.dialog,
    text: 'I tend this little chapel when my road brings me by. The parish church is a good walk from here, so the village sees a priest only now and then.',
    options: [
      { text: 'Who do you answer to?', nextDialogId: 'trier' },
      { text: 'Why do you want fish?', nextDialogId: 'fish_reason' },
    ],
  },
  {
    id: 'trier',
    type: DialogType.dialog,
    text: 'I answer to the Archbishop of Trier, my child, as the churches along the Alzette do. Trier is far, and the road is long, so most days it is only me and the saints.',
    options: [{ text: 'I see.', nextDialogId: 'fish_reason' }],
  },
  {
    id: 'fish_reason',
    type: DialogType.dialog,
    text: 'On a fast day we may not eat meat, so we eat fish instead. A fresh fish feeds a poor soul in the village, and there are always hungry mouths.',
    options: [
      { text: 'Is the water safe?', nextDialogId: 'creature' },
      { text: 'Then take this fish, father.', nextDialogId: resolveGiveNode, condition: canOfferFish },
    ],
  },
  {
    id: 'creature',
    type: DialogType.dialog,
    text: 'They say a lady lives in the Alzette, half woman and half fish. She sings to men at the water and draws them under. I bless a charm for any soul who goes there, true or no.',
    options: [
      { text: 'I have a fish for you, father.', nextDialogId: resolveGiveNode, condition: canOfferFish },
      { text: 'I will take care.', nextDialogId: 'start' },
    ],
  },
  {
    id: 'mutter',
    type: DialogType.description,
    text: "The priest does not seem to notice you. He worries to himself, half aloud. 'A fast day soon, and nothing in the pot. A fish would be a mercy, if anyone thought to bring one.' Then he sees you, and straightens.",
    options: [
      { text: 'Greet him.', nextDialogId: 'greet' },
      { text: 'Leave him be.', nextDialogId: 'start' },
    ],
  },
  giveNode('chub'),
  giveNode('trout'),
  giveNode('eel'),
  giveNode('pike'),
];
